/**
 * API エンドポイント設定
 * 環境別にエンドポイントパスとベースURLを管理
 */

// エンドポイントパス定義
export const ENDPOINTS = {
  AUTH: {
    LOGIN: '/api/auth/login',
    LOGOUT: '/api/auth/logout',
    PASSWORD_RESET: '/api/auth/password-reset',
    SEND_VERIFICATION: '/api/auth/send-verification',
    VERIFY_CODE: '/api/auth/verify',
    REFRESH_TOKEN: '/api/auth/refresh'
  },
  // 公開API（認証不要）
  PUBLIC: {
    PDF: '/api/public/pdf',
    MANUALS: '/api/public/manuals',
    CONFIG: '/api/public/config'
  },
  MEMBER: {
    REGISTER: '/api/members/register',
    PROFILE: '/api/members/{memberId}',
    UPDATE_PROFILE: '/api/members/{memberId}',
    ME: '/api/me',
    PASSWORD: '/api/me/password',
    LIST: '/api/members',
    // Firebase連携用エンドポイント
    SEARCH: '/api/members/search',
    BY_EMAIL: '/api/members/search?email={email}',
    BY_FIREBASE_UID: '/api/members/search?firebaseUid={firebaseUid}'
  },
  // CSRFトークン関連エンドポイント
  CSRF: {
    TOKEN: '/api/csrf/csrf-token',
    REVOKE: '/api/csrf/revoke',
    REVOKE_ALL: '/api/csrf/revoke-all',
    STATUS: '/api/csrf/status' 
  },
  CREDIT_CARD: {
    REGISTER: '/api/v1/payment/card-register',
    GET_INFO: '/api/v1/payment/{customerId}',
    UPDATE: '/api/v1/payment/update',
    DELETE: '/api/v1/payment/card/delete',
    LIST: '/api/v1/payment/list/{customerId}',
    CARD_CHANGE: '/api/v1/payment/card-change'
  },
  USER: {
    PROFILE: '/api/user/profile',
    UPDATE_PROFILE: '/api/user/profile',
    CHANGE_PASSWORD: '/api/user/change-password'
  },
  MANUAL: {
    GET_URL: '/api/me/manual-url',
    LIST: '/api/me/manuals',
    ALL: '/api/me/manuals/all'
  },
  HEALTH: '/health',
};

// フロントエンドルート定義
export const ROUTES = {
  // 認証・登録フロー
  TERMS: '/terms',
  AUTH: '/auth',
  REGISTER: '/register',
  CREDIT_CARD_INPUT: '/credit-card-input',
  REGISTRATION_COMPLETE: '/registration-complete',

  // 認証関連
  LOGIN: '/login',
  LOGOUT: '/logout',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  
  // メインページ
  HOME: '/home',

  // 会員情報編集
  MEMBER_EDIT: '/member-edit',
  CREDIT_CARD_EDIT: '/credit-card-edit',
  
  // エラーページ
  NOT_FOUND: '*'
};

// 環境別ベースURL設定
const BASE_URLS = {
  development: {
    baseUrl: '',
    timeout: 5000
  },
  staging: {
    baseUrl: '',
    timeout: 10000
  },
  production: {
    baseUrl: '',
    timeout: 15000
  }
};

/**
 * 環境別設定を取得
 */
export const getEnvironmentConfig = () => {
  const env = process.env.REACT_APP_ENVIRONMENT || 
              process.env.NODE_ENV || 
              'development';

  // 環境変数が設定されていない場合の警告
  if (!process.env.REACT_APP_API_BASE_URL && env !== 'development') {
    console.warn('⚠️ REACT_APP_API_BASE_URL is not set. Please configure your environment variables.');
  }

  const config = BASE_URLS[env] || BASE_URLS.development;
  
  return {
    ...config,
    endpoints: ENDPOINTS,
    routes: ROUTES,
    environment: env
  };
};

/**
 * 指定環境の設定を取得
 * @param {string} targetEnv - 対象環境
 * @returns {Object} 環境設定
 */
export const getConfigForEnvironment = (targetEnv) => {
  const config = BASE_URLS[targetEnv];
  if (!config) {
    throw new Error(`Unknown environment: ${targetEnv}`);
  }

  return {
    ...config,
    endpoints: ENDPOINTS,
    routes: ROUTES,
    environment: targetEnv
  };
};

/**
 * カスタム設定でクライアントを作成
 * @param {Object} customConfig - カスタム設定
 * @returns {Object} カスタム設定
 */
export const createCustomConfig = (customConfig) => {
  const defaultConfig = getEnvironmentConfig();
  
  return {
    ...defaultConfig,
    ...customConfig,
    endpoints: {
      ...defaultConfig.endpoints,
      ...customConfig.endpoints
    },
    routes: {
      ...defaultConfig.routes,
      ...customConfig.routes
    },
    environment: customConfig.environment || defaultConfig.environment
  };
};

/**
 * モック環境用の設定を取得
 * @returns {Object} モック設定
 */
export const getMockConfig = () => {
  return {
    baseUrl: 'http://localhost:8080',
    timeout: 3000,
    endpoints: {
      ...ENDPOINTS,
      AUTH: {
        LOGIN: '/mock/auth/login',
        LOGOUT: '/mock/auth/logout',
        PASSWORD_RESET: '/mock/auth/password-reset',
        SEND_VERIFICATION: '/mock/auth/send',
        VERIFY_CODE: '/mock/auth/verify',
        REFRESH_TOKEN: '/mock/auth/refresh'
      },
      CREDIT_CARD: {
        REGISTER: '/api/creditcard/register',
        GET_INFO: '/api/creditcard/{customerId}',
        UPDATE: '/api/creditcard/update',
        DELETE: '/api/creditcard/delete'
      }
    },
    routes: ROUTES,
    environment: 'mock'
  };
};

/**
 * クレジットカードAPI専用の設定を取得
 */
export const getCreditCardApiConfig = () => {
  const config = getEnvironmentConfig();
  return {
    ...config,
    endpoints: config.endpoints.CREDIT_CARD
  };
};

/**
 * 環境変数チェッカー
 */
export const checkEnvironmentVariables = () => {
  const requiredVars = {
    'REACT_APP_API_BASE_URL': process.env.REACT_APP_API_BASE_URL,
    'REACT_APP_ENVIRONMENT': process.env.REACT_APP_ENVIRONMENT,
    'NODE_ENV': process.env.NODE_ENV
  };

  const missing = [];
  const present = [];

  Object.entries(requiredVars).forEach(([key, value]) => {
    if (value) {
      present.push({ key, value });
    } else {
      missing.push(key);
    }
  });

  return {
    allSet: missing.length === 0,
    missing,
    present,
    report: () => {
      console.group('🔧 Environment Variables Check');
      
      if (missing.length > 0) {
        console.warn('⚠️ Missing variables:');
        missing.forEach(key => {
          console.warn(`  ${key}`);
        });
      }
      
      console.groupEnd();
    }
  };
};

const apiEndpoints = {
  ENDPOINTS,
  ROUTES,
  getEnvironmentConfig,
  getConfigForEnvironment,
  createCustomConfig,
  getMockConfig,
  getCreditCardApiConfig,
  checkEnvironmentVariables
};

export default apiEndpoints;